
import { GoogleGenAI } from "@google/genai";

const getApiKey = () => {
  try {
    const key = process.env.API_KEY;
    if (key && key !== 'undefined' && key.trim().length > 5) return key;
  } catch (e) {
    return undefined;
  }
  return undefined;
};

// Gecureerde lijst met professionele tech-foto's voor de Smart Mode fallback
const FALLBACK_IMAGES: Record<string, string> = {
  audio: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?auto=format&fit=crop&q=80&w=1000",
  licht: "https://images.unsplash.com/photo-1516280440614-37939bbacd81?auto=format&fit=crop&q=80&w=1000",
  keuken: "https://images.unsplash.com/photo-1556910103-1c02745aae4d?auto=format&fit=crop&q=80&w=1000",
  dj: "https://images.unsplash.com/photo-1516280440614-37939bbacd81?auto=format&fit=crop&q=80&w=1000",
  pioneer: "https://images.unsplash.com/photo-1571266028243-e4733b0f0bb1?auto=format&fit=crop&q=80&w=1000",
  stroom: "https://images.unsplash.com/photo-1544724569-5f546fd6f2b5?auto=format&fit=crop&q=80&w=1000",
  default: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80&w=1000" // Professioneel magazijn
};

const getSmartFallbackImage = (query: string): string => {
  const q = query.toLowerCase();
  if (q.includes('pioneer') || q.includes('cdj')) return FALLBACK_IMAGES.pioneer;
  if (q.includes('dj') || q.includes('mix')) return FALLBACK_IMAGES.dj;
  if (q.includes('audio') || q.includes('speaker') || q.includes('geluid')) return FALLBACK_IMAGES.audio;
  if (q.includes('licht') || q.includes('lamp') || q.includes('spot')) return FALLBACK_IMAGES.licht;
  if (q.includes('keuken') || q.includes('oven') || q.includes('kook')) return FALLBACK_IMAGES.keuken;
  if (q.includes('stroom') || q.includes('aggregaat') || q.includes('kabel')) return FALLBACK_IMAGES.stroom;
  return FALLBACK_IMAGES.default;
};

const generateLocalResponse = (query: string): string => {
  const q = query.toLowerCase();
  const begroetingen = ["Zeker weten!", "Geen probleem, regelen we!", "Ah, goede keuze!", "Deniz heeft 'm klaarstaan!", "Top ding is dat!"];
  const afsluiters = ["Komt helemaal goed, houdoe!", "Ik zet de koffie alvast aan in het magazijn.", "Echt een beest van een machine.", "Brabantse kwaliteit, hè!", "Regelen we direct voor je!"];
  
  let detail = "Deniz heeft dit in topconditie in het magazijn liggen. Hij heeft werkelijk alles voor je event.";
  
  if (q.includes('pioneer') || q.includes('dj')) detail = "De faders zijn gisteren nog gesmeerd en de firmware is up-to-date. Die gaat knallen op je feest!";
  else if (q.includes('licht') || q.includes('lamp')) detail = "Deze units geven zóveel sfeer, je event verandert direct in een droomwereld. Altijd inclusief reserve-kabels.";
  else if (q.includes('audio') || q.includes('speaker')) detail = "Kraakhelder geluid met een bas waar je u tegen zegt. Perfect voor zowel spraak als harde beats.";
  else if (q.includes('keuken') || q.includes('oven')) detail = "Professionele horeca-kwaliteit. Deze unit bakt alles perfect af, van luxe hapjes tot worstenbroodjes.";

  const begroeting = begroetingen[Math.floor(Math.random() * begroetingen.length)];
  const afsluiter = afsluiters[Math.floor(Math.random() * afsluiters.length)];
  return `${begroeting} ${detail} ${afsluiter}`;
};

export const getDenizConfirmation = async (query: string) => {
  const apiKey = getApiKey();
  if (!apiKey) return generateLocalResponse(query);

  try {
    const ai = new GoogleGenAI({ apiKey });
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: `Jij bent Deniz van DenizVerhuurt.nl, een vrolijke tech-expert uit Eindhoven. De gebruiker zoekt: "${query}". Bevestig enthousiast dat je dit verhuurt. Noem een technisch detail. Kort (max 2 zinnen), Brabants gezellig.`,
      config: { temperature: 0.8 },
    });
    return response.text || generateLocalResponse(query);
  } catch (error) {
    return generateLocalResponse(query);
  }
};

export const generateProductImage = async (query: string): Promise<string | null> => {
  const apiKey = getApiKey();
  // ALTIJD een afbeelding teruggeven, of via AI of via Smart Fallback
  if (!apiKey) return getSmartFallbackImage(query);

  try {
    const ai = new GoogleGenAI({ apiKey });
    const response = await ai.models.generateContent({
      model: 'gemini-2.5-flash-image',
      contents: { parts: [{ text: `A professional studio photograph of ${query} in a high-end event warehouse.` }] },
      config: { imageConfig: { aspectRatio: "16:9" } }
    });

    if (response.candidates?.[0]?.content?.parts) {
      for (const part of response.candidates[0].content.parts) {
        if (part.inlineData) return `data:image/png;base64,${part.inlineData.data}`;
      }
    }
    return getSmartFallbackImage(query);
  } catch (error) {
    return getSmartFallbackImage(query);
  }
};
